/**
 * Детайл на разговор (Раздел 8).
 *
 * Пълната транскрипция и свалянето на аудио се показват само на роли с право
 * за това — сървърът също ги отказва, това тук е само скриване.
 */

import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  List,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { CriterionScore, RecordingDetail, TranscriptResponse } from '../api/types';
import { formatDateTime, formatDuration } from './RecordingsPage';

export function RecordingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['recording', id],
    queryFn: () => apiRequest<RecordingDetail>(`/api/recordings/${id!}`),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data?.recording.status === 'PROCESSING' ||
      query.state.data?.recording.status === 'QUEUED'
        ? 10_000
        : false,
  });

  const transcript = useQuery({
    queryKey: ['transcript', id],
    queryFn: () => apiRequest<TranscriptResponse>(`/api/recordings/${id!}/transcript`),
    enabled: Boolean(id) && can('recordings:transcript') && detail.data?.recording.status === 'COMPLETED',
  });

  const reprocess = useMutation({
    mutationFn: () => apiRequest(`/api/recordings/${id!}/reprocess`, { method: 'POST' }),
    onSuccess: async () => {
      notifications.show({ message: 'Записът е пуснат за повторна обработка', color: 'blue' });
      await queryClient.invalidateQueries({ queryKey: ['recording', id] });
    },
    onError: (error: Error) => {
      notifications.show({ message: error.message, color: 'red' });
    },
  });

  const downloadAudio = async (): Promise<void> => {
    try {
      const { url } = await apiRequest<{ url: string }>(`/api/recordings/${id!}/audio-url`);
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      notifications.show({
        message: error instanceof Error ? error.message : 'Свалянето не беше възможно',
        color: 'red',
      });
    }
  };

  if (detail.isLoading) return <Loader />;
  if (detail.isError || !detail.data) return <Alert color="red">Записът не беше зареден</Alert>;

  const { recording, extension, analysis, criterionScores, jobs } = detail.data;
  const failedJob = jobs.find((job) => job.status === 'FAILED');

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Разговор от {formatDateTime(recording.callStartedAt)}</Title>
        <Group>
          {can('recordings:download') && !recording.audioDeletedAt && (
            <Button variant="default" onClick={() => void downloadAudio()}>
              Свали аудиото
            </Button>
          )}
          {can('recordings:reprocess') && (
            <Button
              variant="light"
              loading={reprocess.isPending}
              onClick={() => reprocess.mutate()}
            >
              Повторна обработка
            </Button>
          )}
        </Group>
      </Group>

      {failedJob && (
        <Alert color="red" title="Обработката се провали">
          <Text size="sm">
            Етап {failedJob.stage}: {failedJob.errorCode}
          </Text>
          {failedJob.errorMessage && (
            <Text size="sm" c="dimmed">
              {failedJob.errorMessage}
            </Text>
          )}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 2, md: 4 }}>
        <Field label="Служител" value={`${extension.employeeName} (вътр. ${extension.extension})`} />
        <Field label="Група" value={extension.groupName ?? '—'} />
        <Field label="Посока" value={recording.direction === 'IN' ? 'Входящо' : 'Изходящо'} />
        <Field label="Телефон" value={recording.customerMsisdn} />
        <Field label="Времетраене" value={formatDuration(recording.durationMs)} />
        <Field label="Файл" value={recording.originalFilename} />
        <Field label="Статус" value={recording.status} />
        <Field
          label="Обща оценка"
          value={analysis ? String(analysis.overallScore) : '—'}
          highlight={analysis?.isFlagged ? 'red' : undefined}
        />
      </SimpleGrid>

      {analysis && (
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Title order={5}>Резюме</Title>
            {analysis.isFlagged && <Badge color="red">Проблемен разговор</Badge>}
          </Group>
          <Text>{analysis.summary}</Text>

          {analysis.recommendations.length > 0 && (
            <>
              <Title order={6} mt="md">
                Препоръки
              </Title>
              <List size="sm">
                {analysis.recommendations.map((recommendation) => (
                  <List.Item key={recommendation}>{recommendation}</List.Item>
                ))}
              </List>
            </>
          )}

          <Text size="xs" c="dimmed" mt="sm">
            Модел: {analysis.model} · prompt: {analysis.promptVersion}
          </Text>
        </Card>
      )}

      {criterionScores.length > 0 && (
        <Card withBorder>
          <Title order={5} mb="sm">
            Оценка по критерии
          </Title>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={260}>Критерий</Table.Th>
                <Table.Th w={110}>Оценка</Table.Th>
                <Table.Th>Аргументация и цитати</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {criterionScores.map((score) => (
                <Table.Tr key={score.id}>
                  <Table.Td>{score.criterionNameBg}</Table.Td>
                  <Table.Td>
                    <ScoreBadge score={score} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{score.rationale}</Text>
                    {score.evidence.map((quote) => (
                      <Text key={quote} size="xs" c="dimmed" fs="italic" mt={4}>
                        „{quote}“
                      </Text>
                    ))}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {can('recordings:transcript') && (
        <Card withBorder>
          <Title order={5} mb="sm">
            Транскрипция
          </Title>

          {transcript.isLoading && <Loader size="sm" />}
          {transcript.isError && <Text c="dimmed">Транскрипцията не е налична.</Text>}

          {transcript.data && (
            <>
              <Text size="xs" c="dimmed" mb="sm">
                STT модел: {transcript.data.sttModel}
              </Text>
              <Stack gap="xs">
                {transcript.data.segments.map((segment) => (
                  <Group key={segment.id} align="start" wrap="nowrap">
                    <Text size="xs" c="dimmed" w={120} style={{ flexShrink: 0 }}>
                      {formatTimestamp(segment.startMs)} – {formatTimestamp(segment.endMs)}
                    </Text>
                    <Badge
                      w={110}
                      style={{ flexShrink: 0 }}
                      color={speakerColor(segment.speakerRole)}
                      variant="light"
                    >
                      {speakerLabel(segment.speakerRole, segment.speakerLabel)}
                    </Badge>
                    <Text size="sm">{segment.text}</Text>
                  </Group>
                ))}
              </Stack>
            </>
          )}
        </Card>
      )}

      <Accordion variant="contained">
        <Accordion.Item value="jobs">
          <Accordion.Control>Етапи на обработката</Accordion.Control>
          <Accordion.Panel>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Етап</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th>Опити</Table.Th>
                  <Table.Th>Токени (вх./изх./разсъждение)</Table.Th>
                  <Table.Th>Грешка</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {jobs.map((job) => (
                  <Table.Tr key={job.stage}>
                    <Table.Td>{job.stage}</Table.Td>
                    <Table.Td>{job.status}</Table.Td>
                    <Table.Td>{job.attempt}</Table.Td>
                    <Table.Td>
                      {job.aiTokensIn ?? '—'} / {job.aiTokensOut ?? '—'} /{' '}
                      {job.aiReasoningTokens ?? '—'}
                    </Table.Td>
                    <Table.Td>{job.errorCode ?? '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {recording.audioDeletedAt && (
        <Alert color="gray">
          Аудиото е изтрито по срок на съхранение на {formatDateTime(recording.audioDeletedAt)}.
        </Alert>
      )}
    </Stack>
  );
}

function Field({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: string;
}) {
  return (
    <Paper withBorder p="sm">
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text fw={500} c={highlight}>
        {value}
      </Text>
    </Paper>
  );
}

function ScoreBadge({ score }: { score: CriterionScore }) {
  if (score.scaleType === 'BOOLEAN') {
    return (
      <Badge color={score.score === 1 ? 'green' : 'gray'}>{score.score === 1 ? 'да' : 'не'}</Badge>
    );
  }

  const color = score.score >= 4 ? 'green' : score.score === 3 ? 'yellow' : 'red';
  return <Badge color={color}>{score.score} / 5</Badge>;
}

function speakerLabel(role: string | null, rawLabel: string): string {
  if (role === 'EMPLOYEE') return 'Служител';
  if (role === 'CUSTOMER') return 'Клиент';
  if (role === 'OTHER') return 'Друг';
  return rawLabel;
}

function speakerColor(role: string | null): string {
  if (role === 'EMPLOYEE') return 'blue';
  if (role === 'CUSTOMER') return 'teal';
  return 'gray';
}

function formatTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}.${String(ms % 1000).padStart(3, '0')}`;
}
